import { makeAutoObservable, runInAction } from "mobx";
import { SearchProducts } from "../models/Products";
import agent from "../api/agent";
import { store } from "./store";

export interface Category {
  id: number;
  name: string;
  is_active?: boolean;
}

export interface SubCategory {
  id: number;
  name: string;
  categoryId?: number;
  is_active?: boolean;
}

export interface AutocompleteProduct {
  id: string;
  name: string;
  category: string;
  subcategory: string;
  brand: string;
  collection: string;
  suggestionText: string;
}

export default class ProductStore {
  products: SearchProducts | null = null;
  deliveryType: number = 1; // 1 for Standard, 1 for Quick
  loadingProducts: boolean = false;
  categories: Category[] = [];
  subCategories: SubCategory[] = [];
  loadingCategories: boolean = false;
  loadingSubCategories: boolean = false;
  loadedSubCategoriesForCategory: Set<number> = new Set();
  status: number = 1;
  selectedCategoryIds: number[] = [];
  selectedSubCategoryNames: string[] = [];
  autocompleteSuggestions: AutocompleteProduct[] = [];
  loadingAutocomplete: boolean = false;
  outletCustomerTypeId: number | null = null; // customerTypeId from selected outlet (fallback)

  constructor() {
    makeAutoObservable(this);
  }

  setOutletCustomerTypeId = (customerTypeId: number | null) => {
    this.outletCustomerTypeId = customerTypeId;
  };

  searchProducts = async (searchQuery: string = "") => {
    this.setLoadingProducts(true);
    this.products = null;
    try {
      const warehouse = store?.commonStore?.warehouseId;
      
      if (!warehouse) {
        console.error("Warehouse ID not available");
        this.setLoadingProducts(false);
        return;
      }

      const params = new URLSearchParams();
      // Use customerTypeId from admin (verify-admin-otp) first, fallback to outlet's customerTypeId
      const businessTypeId = store.commonStore.customerTypeId ?? this.outletCustomerTypeId;
      if (businessTypeId != null) {
        params.append(
          "businessTypeId",
          String(businessTypeId)
        );
      }
      params.append("delivery_type", String(this.deliveryType));
      if (store.commonStore.isDirectDelivery !== null) {
        params.append("isDD", store.commonStore.isDirectDelivery ? "true" : "false");
      }
      params.append("warehouse_id", String(warehouse));
      params.append("page", "1");
      
      // Add search query if provided
      if (searchQuery) {
        params.append("search", searchQuery);
      }

      if (this.selectedSubCategoryNames.length > 0) {
        params.append("fieldname", "subcategory_name");
        params.append("fieldvalue", this.selectedSubCategoryNames.join("|"));
      } else if (this.selectedCategoryIds.length > 0) {
        params.append("category_ids", this.selectedCategoryIds.join(","));
      }

      const headers = {
        outletId: store.commonStore.customer?.id || 0,
      };

      const result = await agent.Product.search(params, headers);
      runInAction(() => {
        this.products = result;
      });
      this.setLoadingProducts(false);
    } catch (error) {
      console.error("Error searching products:", error);
      this.setLoadingProducts(false);
    }
  };

  setFilters = (categoryIds: number[], subCategoryNames: string[]) => {
    this.selectedCategoryIds = categoryIds;
    this.selectedSubCategoryNames = subCategoryNames;
  };

  clearFilters = () => {
    this.selectedCategoryIds = [];
    this.selectedSubCategoryNames = [];
  };

  loadCategories = async () => {
    this.loadingCategories = true;
    try {
      const warehouse = store?.commonStore?.warehouseId;
      const params = new URLSearchParams();
      params.append("warehouseId", String(warehouse));
      params.append("status", String(this.status));

      const headers = {
        customerTypeId: store.commonStore.customer?.customerTypeId || 0,
        customerOrgId: store.commonStore.customer?.customerOrgId || 0,
        outletId: store.commonStore.customer?.id || 0,
      };

      const categoriesResult = await agent.Product.getAllCategory(params, headers);

      runInAction(() => {
        if (categoriesResult?.data && Array.isArray(categoriesResult.data)) {
          const categoryMap = new Map<number, Category>();
          categoriesResult.data.forEach((category: any) => {
            if (category.id && category.name) {
              categoryMap.set(category.id, {
                id: category.id,
                name: category.name,
                is_active: false,
              });
            }
          });
          this.categories = Array.from(categoryMap.values());
        }
        this.loadingCategories = false;
      });
    } catch (error) {
      console.error("Error loading categories:", error);
      runInAction(() => {
        this.loadingCategories = false;
      });
    }
  };

  loadSubCategoriesForCategory = async (categoryId: number) => {
    // Don't load if already loaded
    if (this.loadedSubCategoriesForCategory.has(categoryId)) {
      return;
    }

    this.loadingSubCategories = true;
    try {
      const warehouse = store?.commonStore?.warehouseId;
      const params = new URLSearchParams();
      params.append("warehouseId", String(warehouse));
      params.append("status", String(this.status));
      params.append("category_id", String(categoryId));

      const headers = {
        customerTypeId: store.commonStore.customer?.customerTypeId || 0,
        customerOrgId: store.commonStore.customer?.customerOrgId || 0,
        outletId: store.commonStore.customer?.id || 0,
      };

      const subCategoriesResult = await agent.Product.getAllSubCategory(params, headers);

      runInAction(() => {
        if (subCategoriesResult?.data && Array.isArray(subCategoriesResult.data)) {
          const newSubCategories: SubCategory[] = [];
          subCategoriesResult.data.forEach((subCategory: any) => {
            if (
              subCategory.id &&
              subCategory.name &&
              subCategory.category_id === categoryId
            ) {
              // Only add subcategories for this specific category
              const subCat: SubCategory = {
                id: subCategory.id,
                name: subCategory.name,
                categoryId: subCategory.category_id,
                is_active: false,
              };
              
              // Check if this subcategory already exists
              if (!this.subCategories.some(s => s.id === subCat.id)) {
                newSubCategories.push(subCat);
              }
            }
          });

          // Add new subcategories to the existing list
          this.subCategories = [...this.subCategories, ...newSubCategories];
          
          // Mark this category as loaded
          this.loadedSubCategoriesForCategory.add(categoryId);
        }
        this.loadingSubCategories = false;
      });
    } catch (error) {
      console.error("Error loading subcategories:", error);
      runInAction(() => {
        this.loadingSubCategories = false;
      });
    }
  };

  autocomplete = async (searchQuery: string) => {
    if (!searchQuery || searchQuery.trim().length === 0) {
      this.clearAutocomplete();
      return;
    }

    this.loadingAutocomplete = true;
    try {
      const params = new URLSearchParams();
      params.append("search", searchQuery);

      const headers = {
        customerTypeId: store.commonStore.customer?.customerTypeId || 0,
        customerOrgId: store.commonStore.customer?.customerOrgId || 0,
        outletId: store.commonStore.customer?.id || 0,
      };

      const result = await agent.Product.autocomplete(params, headers);

      runInAction(() => {
        const products: any[] = result?.data?.products ?? [];
        const suggestions: any[] = result?.data?.suggestions ?? [];
        const suggestionText: string = suggestions[0]?.text ?? searchQuery;

        if (products.length > 0) {
          const seenSubcategories = new Set<string>();
          this.autocompleteSuggestions = products
            .filter((item: any) => {
              if (!item.subcategory || seenSubcategories.has(item.subcategory)) {
                return false;
              }
              seenSubcategories.add(item.subcategory);
              return true;
            })
            .map((item: any) => ({
              id: item.id,
              name: item.name,
              category: item.category,
              subcategory: item.subcategory,
              brand: item.brand,
              collection: item.collection,
              suggestionText,
            }));
        } else {
          this.autocompleteSuggestions = [];
        }
        this.loadingAutocomplete = false;
      });
    } catch (error) {
      console.error("Error fetching autocomplete suggestions:", error);
      runInAction(() => {
        this.autocompleteSuggestions = [];
        this.loadingAutocomplete = false;
      });
    }
  };

  clearAutocomplete = () => {
    this.autocompleteSuggestions = [];
    this.loadingAutocomplete = false;
  };

  changeDeliveryType = (deliveryType: number): void => {
    this.deliveryType = deliveryType;
  };

  setLoadingProducts = (val: boolean) => {
    this.loadingProducts = val;
  };
}
